/**
 * La source « session » des présences : la coordination, connectée, pour un
 * centre et une semaine qu'elle a choisis.
 *
 * Tout se lit et s'écrit dans les tables avec les droits du rôle (la RLS
 * ouvre `presences`, `kids`, `kid_*`, `authorized_persons` et les photos à
 * l'administration et à la coordination). Aucune fonction serveur : c'est
 * l'écran de gestion d'avant, avec les écrans des animateurs devant.
 *
 * Les écritures ne touchent que la case ou la note visée, et portent le nom
 * de la personne (`recorded_by_user_id`) ; `lien_presences_id` repasse à nul.
 * Deux personnes sur le même enfant au même instant : la dernière écriture
 * gagne, et chacune revoit ce que la base a gardé.
 */
import { db } from '../payments/db';
import { sansJpa, stagesDesJpa } from '../jpa/stages.supabase';
import type { JourCle, LectureDeFiche, LectureDuLien } from './lien';
import type { LectureDesEssentiels } from './essentiels';
import type { LectureDesPhotos, ResultatDuGeste, ResultatDuGroupe } from './lien.supabase';
import type { SourceDesPresences } from './source';
import {
  assemblerEssentiels,
  assemblerFiche,
  assemblerListe,
  estVivante,
  presenceDepuisLaLigne,
  semainesDuCentre,
  type ActivitesLues,
  type AllergiesLues,
  type DepartLu,
  type ImageLue,
  type InclusionLue,
  type InscriptionLue,
  type PersonneLue,
  type PresenceLue,
  type SanteLue,
  type SemaineDuCentre,
  type SessionDuCentre,
  type TablesDesEnfants,
} from './session';

const PAR_LOT = 200;
const PHOTOS_DUREE_S = 3600;

const lots = <T,>(xs: readonly T[]): T[][] => {
  const r: T[][] = [];
  for (let i = 0; i < xs.length; i += PAR_LOT) r.push(xs.slice(i, i + PAR_LOT));
  return r;
};

/** `select … in (…)` par lots de 200 identifiants. (Un appel PostgREST est un thenable, pas une Promise.) */
async function lireParLots<T>(lire: (ids: string[]) => PromiseLike<{ data: unknown; error: unknown }>, ids: readonly string[]): Promise<T[]> {
  const tout: T[] = [];
  for (const lot of lots([...new Set(ids)])) {
    if (lot.length === 0) continue;
    const { data, error } = await lire(lot);
    if (error) throw error;
    tout.push(...((data ?? []) as T[]));
  }
  return tout;
}

/* ------------------------------------------------------------------------ */
/*  Les centres et leurs semaines                                            */
/* ------------------------------------------------------------------------ */

export interface CentreDePresences {
  id: string;
  nom: string;
}

/** Les centres de vacances actifs, par nom. */
export async function lireLesCentres(): Promise<CentreDePresences[]> {
  const { data, error } = await db
    .from('centers')
    .select('id, name')
    .eq('active', true)
    .eq('archived', false)
    .eq('centre_de_vacances', true)
    .order('name');
  if (error) throw error;
  return ((data ?? []) as { id: string; name: string }[]).map((c) => ({ id: c.id, nom: c.name }));
}

interface SessionLue {
  id: string;
  stage_id: string;
  start_date: string;
  end_date: string;
  periode: string;
  semaine: string | null;
  stages: { title: string } | { title: string }[] | null;
}

const titre = (s: SessionLue['stages']): string => (Array.isArray(s) ? s[0]?.title : s?.title) ?? '';

/** Les sessions d'un centre (stages et plaines, pas les journées pédagogiques), et ses semaines. */
export async function lireLesSemaines(centreId: string): Promise<SemaineDuCentre[]> {
  const { data, error } = await sansJpa(
    db
      .from('sessions')
      .select('id, stage_id, start_date, end_date, periode, semaine, stages(title)')
      .eq('center_id', centreId)
      .eq('active', true)
      .eq('archived', false)
      .order('start_date'),
    await stagesDesJpa(),
  );
  if (error) throw error;
  const sessions: SessionDuCentre[] = ((data ?? []) as unknown as SessionLue[]).map((s) => ({
    id: s.id,
    stageId: s.stage_id,
    stage: titre(s.stages),
    debut: s.start_date,
    fin: s.end_date,
    periode: s.periode,
    semaine: s.semaine,
  }));
  return semainesDuCentre(sessions);
}

/* ------------------------------------------------------------------------ */
/*  La source                                                                */
/* ------------------------------------------------------------------------ */

interface InscriptionBrute {
  id: string;
  activity_id: string;
  kid_id: string;
  user_id: string | null;
  cancellation_status: string | null;
  subgroup_name: string | null;
  kids: { prenom: string | null; nom: string | null; photo_url: string | null } | { prenom: string | null; nom: string | null; photo_url: string | null }[] | null;
  subgroups: { name: string } | { name: string }[] | null;
}

const un = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);

function inscriptionDepuisLaLigne(r: InscriptionBrute): InscriptionLue {
  const k = un(r.kids);
  return {
    id: r.id,
    sessionId: r.activity_id,
    kidId: r.kid_id,
    userId: r.user_id,
    prenom: k?.prenom ?? '',
    nom: k?.nom ?? '',
    photo: k?.photo_url ?? null,
    sousGroupe: un(r.subgroups)?.name ?? r.subgroup_name ?? null,
    cancellationStatus: r.cancellation_status,
  };
}

async function lireLesInscriptions(semaine: SemaineDuCentre): Promise<InscriptionLue[]> {
  const brutes = await lireParLots<InscriptionBrute>(
    (ids) =>
      db
        .from('registrations')
        .select('id, activity_id, kid_id, user_id, cancellation_status, subgroup_name, kids(prenom, nom, photo_url), subgroups(name)')
        .in('activity_id', ids),
    semaine.sessions.map((s) => s.id),
  );
  return brutes.map(inscriptionDepuisLaLigne).filter((i) => estVivante(i.cancellationStatus));
}

async function lireLesTables(kidIds: readonly string[]): Promise<TablesDesEnfants> {
  const [allergies, sante, departs, images] = await Promise.all([
    lireParLots<AllergiesLues>((ids) => db.from('kid_allergies').select('kid_id, has_allergies, allergies_details, allergies_consequences, special_diet, diet_details').in('kid_id', ids), kidIds),
    lireParLots<SanteLue>((ids) => db.from('kid_health').select('kid_id, medication, medication_details, medication_autonomy, specific_medical, past_medical, tetanus, doctor_name, doctor_phone').in('kid_id', ids), kidIds),
    lireParLots<DepartLu>((ids) => db.from('kid_departure').select('kid_id, leaves_alone, departure_time, pickup_people_ids').in('kid_id', ids), kidIds),
    lireParLots<ImageLue>((ids) => db.from('kid_image_rights').select('kid_id, internal_spaces, activity_reports, public_website, social_media, print_materials, consented_at').in('kid_id', ids), kidIds),
  ]);
  const personneIds = departs.flatMap((d) => d.pickup_people_ids ?? []);
  const personnes = await lireParLots<PersonneLue>(
    (ids) => db.from('authorized_persons').select('id, first_name, last_name, phone_number, relationship').in('id', ids),
    personneIds,
  );
  return { allergies, sante, departs, personnes, images };
}

const COLONNE: Record<JourCle, string> = {
  monday: 'monday_present',
  tuesday: 'tuesday_present',
  wednesday: 'wednesday_present',
  thursday: 'thursday_present',
  friday: 'friday_present',
};

const SELECTION_PRESENCE = 'registration_id, monday_present, tuesday_present, wednesday_present, thursday_present, friday_present, notes';

export function sourceDeLaSession(args: {
  centre: CentreDePresences;
  semaine: SemaineDuCentre;
  userId: string | null;
}): SourceDesPresences {
  const { centre, semaine, userId } = args;
  const sessionIds = new Set(semaine.sessions.map((s) => s.id));
  let inscriptions: InscriptionLue[] = [];

  /* L'inscription doit être du centre et de la semaine : la source ne pointe pas ailleurs. */
  const inscriptionDe = async (registrationId: string): Promise<InscriptionLue> => {
    let i = inscriptions.find((x) => x.id === registrationId);
    if (!i) {
      inscriptions = await lireLesInscriptions(semaine);
      i = inscriptions.find((x) => x.id === registrationId);
    }
    if (!i || !sessionIds.has(i.sessionId)) throw new Error('inscription_hors_lien');
    return i;
  };

  /* Une écriture sur la ligne de l'enfant : la case ou la note visée, rien d'autre. */
  const ecrire = async (registrationId: string, champs: Record<string, unknown>): Promise<ResultatDuGeste> => {
    await inscriptionDe(registrationId);
    const base = { ...champs, recorded_by_user_id: userId, lien_presences_id: null };
    const maj = await db.from('presences').update(base).eq('registration_id', registrationId).select(SELECTION_PRESENCE);
    if (maj.error) throw maj.error;
    if (maj.data && maj.data.length > 0) return { codeRequis: false, presence: presenceDepuisLaLigne(maj.data[0] as PresenceLue) };

    const ins = await db.from('presences').insert({ registration_id: registrationId, ...base }).select(SELECTION_PRESENCE).single();
    if (!ins.error) return { codeRequis: false, presence: presenceDepuisLaLigne(ins.data as PresenceLue) };
    if ((ins.error as { code?: string }).code !== '23505') throw ins.error;
    // Quelqu'un a créé la ligne entre-temps : on la met à jour.
    const encore = await db.from('presences').update(base).eq('registration_id', registrationId).select(SELECTION_PRESENCE).single();
    if (encore.error) throw encore.error;
    return { codeRequis: false, presence: presenceDepuisLaLigne(encore.data as PresenceLue) };
  };

  return {
    async lireListe(): Promise<LectureDuLien> {
      const lues = await lireLesInscriptions(semaine);
      const [presences, feries] = await Promise.all([
        lireParLots<PresenceLue>(
          (lot) => db.from('presences').select(SELECTION_PRESENCE).in('registration_id', lot),
          lues.map((i) => i.id),
        ),
        (async () => {
          const { data, error } = await db.from('public_holidays').select('date, name').gte('date', semaine.lundi).lte('date', semaine.vendredi);
          if (error) throw error;
          return (data ?? []) as { date: string; name: string }[];
        })(),
      ]);
      inscriptions = lues;
      return { codeRequis: false, liste: assemblerListe({ centre, semaine, inscriptions: lues, presences, feries }) };
    },

    pointer: (registrationId, jour, present) => ecrire(registrationId, { [COLONNE[jour]]: present }),

    noter: (registrationId, notes) => ecrire(registrationId, { notes: notes.trim() || null }),

    async pointerLesAutres(ids, jour, present): Promise<ResultatDuGroupe> {
      const colonne = COLONNE[jour];
      const permis = ids.filter((id) => inscriptions.some((i) => i.id === id && sessionIds.has(i.sessionId)));
      if (permis.length === 0) return { codeRequis: false, presences: [] };
      const base = { [colonne]: present, recorded_by_user_id: userId, lien_presences_id: null };

      const existantes = await lireParLots<{ registration_id: string }>(
        (lot) => db.from('presences').select('registration_id').in('registration_id', lot),
        permis,
      );
      const avecLigne = new Set(existantes.map((e) => e.registration_id));
      const ecrites: PresenceLue[] = [];

      // Les lignes qui existent et dont la case est encore vide.
      for (const lot of lots(permis.filter((id) => avecLigne.has(id)))) {
        const { data, error } = await db.from('presences').update(base).in('registration_id', lot).is(colonne, null).select(SELECTION_PRESENCE);
        if (error) throw error;
        ecrites.push(...((data ?? []) as PresenceLue[]));
      }
      // Les enfants sans ligne du tout.
      const sansLigne = permis.filter((id) => !avecLigne.has(id));
      if (sansLigne.length > 0) {
        const { data, error } = await db
          .from('presences')
          .insert(sansLigne.map((id) => ({ registration_id: id, ...base })))
          .select(SELECTION_PRESENCE);
        if (error && (error as { code?: string }).code !== '23505') throw error;
        if (!error) ecrites.push(...((data ?? []) as PresenceLue[]));
      }
      return {
        codeRequis: false,
        presences: ecrites.map((p) => ({ ...presenceDepuisLaLigne(p), registrationId: p.registration_id })),
      };
    },

    async lireEssentiels(): Promise<LectureDesEssentiels> {
      if (inscriptions.length === 0) inscriptions = await lireLesInscriptions(semaine);
      const tables = await lireLesTables(inscriptions.map((i) => i.kidId));
      return { codeRequis: false, parInscription: assemblerEssentiels(inscriptions, tables) };
    },

    async lirePhotos(): Promise<LectureDesPhotos> {
      if (inscriptions.length === 0) inscriptions = await lireLesInscriptions(semaine);
      const avecPhoto = inscriptions.filter((i) => i.photo && i.photo.trim());
      const photos = new Map<string, string>();
      if (avecPhoto.length === 0) return { codeRequis: false, photos };
      const chemins = [...new Set(avecPhoto.map((i) => i.photo!.trim()))];
      const { data, error } = await db.storage.from('kid-photos').createSignedUrls(chemins, PHOTOS_DUREE_S);
      if (error) throw error;
      const urlDe = new Map<string, string>();
      (data ?? []).forEach((s, i) => {
        if (s.signedUrl && !s.error) urlDe.set(chemins[i], s.signedUrl);
      });
      for (const i of avecPhoto) {
        const url = urlDe.get(i.photo!.trim());
        if (url) photos.set(i.id, url);
      }
      return { codeRequis: false, photos };
    },

    async lireFiche(registrationId): Promise<LectureDeFiche> {
      const i = await inscriptionDe(registrationId);
      const [enfant, parent, tables, activites, inclusion] = await Promise.all([
        db.from('kids').select('date_naissance, ecole').eq('id', i.kidId).maybeSingle(),
        i.userId ? db.from('users').select('prenom, nom, telephone, telephone2').eq('id', i.userId).maybeSingle() : Promise.resolve({ data: null, error: null }),
        lireLesTables([i.kidId]),
        db.from('kid_activities').select('kid_id, can_participate, restriction_details, can_swim, swim_level, water_fear, other_info').eq('kid_id', i.kidId).maybeSingle(),
        db.from('kid_inclusion').select('kid_id, has_needs, situation_details, impact_details, needs_dedicated_staff, staff_details, strategies, assistive_devices, stress_signals, strengths, previous_experience').eq('kid_id', i.kidId).maybeSingle(),
      ]);
      for (const r of [enfant, parent, activites, inclusion]) if (r.error) throw r.error;
      const e = (enfant.data ?? { date_naissance: null, ecole: null }) as { date_naissance: string | null; ecole: string | null };
      let ecole: string | null = e.ecole;
      if (ecole && /^[0-9a-f-]{36}$/i.test(ecole)) {
        const { data } = await db.from('schools').select('name').eq('id', ecole).maybeSingle();
        ecole = (data as { name: string } | null)?.name ?? null;
      }
      return {
        codeRequis: false,
        fiche: assemblerFiche({
          inscription: i,
          stage: semaine.sessions.find((s) => s.id === i.sessionId)?.stage ?? '',
          enfant: e,
          ecole,
          parent: parent.data as { prenom: string | null; nom: string | null; telephone: string | null; telephone2: string | null } | null,
          tables,
          activites: activites.data as ActivitesLues | null,
          inclusion: inclusion.data as InclusionLue | null,
        }),
      };
    },
  };
}
